import queryString from 'query-string';
import get from 'lodash/get';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_AUTHOR_DETAIL_SET_DATA,
  CATALOG_AUTHOR_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { authorDetail: currentState } = getState();

    const { authorSlug } = match.params;

    let newState = {
      authorSlug,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.author) {
      newState = {
        ...newState,
        ...currentState,
      };
    }
    dispatch(setData(newState));

    if (!newState.author || newState.author.seo_description === undefined) {
      await dispatch(fetchDetail(match));
    }
    // the extra blocks on the author page can be fetched in parallel since they dont block each other
    let promises = [];
    if (!newState.books || !newState.audiobooks) {
      promises.push(dispatch(fetchBookList({})));
    }
    if (!newState.booksets) {
      promises.push(dispatch(fetchBooksetList({})));
    }
    if (!newState.series) {
      promises.push(dispatch(fetchSeriesList({})));
    }
    if (!newState.similarAuthors) {
      promises.push(dispatch(fetchSimilarAuthorList({})));
    }
    await (promises.length > 0 ? Promise.all(promises) : Promise.resolve(null));
  };
}

export function fetchDetail(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { authorSlug } = match.params;
    const url = `/api/authors/s-${authorSlug}/`;

    return await (
      fetchQuery(url, { headers: ssr.headers })
        .then(
          ({ data }) => {
            dispatch(setData({
              author: data,
              isFollowedByUser: get(data, 'is_followed_by_user', false),
            }));
          }
        )
        .catch(
          ({ data, resp, error }) => {
            if (resp.status === 404) {
              dispatch(Error404Actions.show());
            }
            else {
              Raven.captureMessage('failed to load author detail due to server error', {
                level: 'error',
                extra: {
                  status: resp.status,
                  response: data,
                  error,
                },
              });
              throw new Error('failed to load author detail due to server error');
            }
          }
        )
    );
  };
}

export function fetchBookList() {
  return async(dispatch, getState) => {
    const { authorDetail, ssr } = getState();
    const { author } = authorDetail;

    if (!author) {
      return;
    }

    let queryParams = {
      authors: author.id,
      limit: 10,
      o: 'popular',
      author_weight__gte: 0,
    };
    const qs = queryString.stringify(queryParams);
    const url = `/api/books/?${qs}`;

    let pendingState = {
      // clear the book list before the fetch
      books: null,
    };

    dispatch(setData(pendingState));

    const booksFetch = (booktype='text') => {
      const AVAILABLE_TYPES = ['text', 'audio'];
      if (AVAILABLE_TYPES.indexOf(booktype) < 0) {
        return Promise.resolve();
      }

      return fetchQuery(url+`&type=${booktype}`, { apiVersion: 4, headers: ssr.headers })
        .then(
          ({ data }) => {
            dispatch(setData({
              ...(booktype === 'text'
                ?
                {
                  books: data.objects,
                  totalBookCount: data.meta.total_count,
                }
                :
                {
                  audiobooks: data.objects,
                  totalAudioBookCount: data.meta.total_count,
                }
              ),
            }));
          }
        )
        .catch(
          ({ data, resp, error }) => {
            Raven.captureMessage(
              `failed to load author ${booktype === 'text' ? 'books' : 'audiobooks'} due to server error`,
              {
                level: 'error',
                extra: {
                  status: resp.status,
                  response: data,
                  error,
                },
              }
            );
            throw new Error(
              `failed to load author ${booktype === 'text' ? 'books' : 'audiobooks'} due to server error`
            );
          }
        );
    };

    return Promise.all([booksFetch('text'), booksFetch('audio')]);
  };
}

export function fetchBooksetList() {
  return async(dispatch, getState) => {
    const { authorDetail, ssr } = getState();
    const { author } = authorDetail;

    if (!author) {
      return;
    }

    let queryParams = {
      author: author.id,
      limit: 10,
    };
    const qs = queryString.stringify(queryParams);
    const url = `/api/booksets/?${qs}`;

    let pendingState = {
      // clear the book list before the fetch
      booksets: null,
    };

    dispatch(setData(pendingState));

    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(
          ({ data }) => {
            dispatch(setData({
              booksets: data,
            }));
          }
        )
        .catch(
          ({ data, resp, error }) => {
            Raven.captureMessage('failed to load author bookset list due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load author bookset list due to server error');
          }
        )
    );
  };
}

export function fetchSeriesList() {
  return async(dispatch, getState) => {
    const { authorDetail, ssr } = getState();
    const { author } = authorDetail;

    if (!author) {
      return;
    }

    let queryParams = {
      author: author.id,
      limit: 10,
    };

    const qs = queryString.stringify(queryParams);
    const url = `/api/catalog/series/?${qs}`;

    let pendingState = {
      // clear the book list before the fetch
      series: null,
    };

    dispatch(setData(pendingState));

    return (
      fetchQuery(url, { headers: ssr.headers })
        .then(({ data }) => {
          dispatch(setData({
            series: data,
          }));
        })
        .catch(({ data, resp, error }) => {
          Raven.captureMessage('failed to load author series list due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load author series list due to server error');
        })
    );
  };
}

export function fetchSimilarAuthorList() {
  return async(dispatch, getState) => {
    const { authorDetail, ssr } = getState();
    const { author } = authorDetail;

    if (!author) {
      return;
    }

    const url = `/api/reco/similar-authors/${author.id}/`;

    let pendingState = {
      // clear the authors list before the fetch
      similarAuthors: null,
    };

    dispatch(setData(pendingState));

    return (
      fetchQuery(url, { apiVersion: 3, headers: ssr.headers })
        .then(
          ({ data }) => {
            dispatch(setData({
              similarAuthors: data,
            }));
          }
        )
        .catch(
          ({ data, resp, error }) => {
            const errorMessage = 'failed to load similar authors list due to server error';
            Raven.captureMessage(errorMessage, {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error(errorMessage);
          }
        )
    );
  };
}

export const fetchSubscribeUserToAuthor = () => {
  return (dispatch, getState) => {
    const { authorDetail, ssr } = getState();
    const { author } = authorDetail;

    const oprions = {
      method: 'POST',
      headers: ssr.headers,
    };
    const url = `/api/authors/${author.id}/subscribe/`;
    return (
      fetchQuery(url, oprions)
        .then(() => {
          return true;
        })
        .catch(({ data, resp, error }) => {
          const errorMessage = 'failed to subscribe user to authors changes due to server error';
          Raven.captureMessage(errorMessage, {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          return false;
        })
    );
  };
};

export const fetchUnsubscribeUserToAuthor = () => {
  return (dispatch, getState) => {
    const { authorDetail, ssr } = getState();
    const { author } = authorDetail;

    const oprions = {
      method: 'POST',
      headers: ssr.headers,
    };
    const url = `/api/authors/${author.id}/unsubscribe/`;
    return (
      fetchQuery(url, oprions)
        .then(() => {
          return true;
        })
        .catch(({ data, resp, error }) => {
          const errorMessage = 'failed to unsubscribe user to authors changes due to server error';
          Raven.captureMessage(errorMessage, {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          return false;
        })
    );
  };
};

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { authorDetail, payments } = getState();
    const { author } = authorDetail;

    const standardPrice = get(payments, 'prices.standard["1"].full_price', '');

    const desktopUrl = `https://${BASE_HOST}${author.absolute_url}`;
    const cover206x208 = author.photo ? cropStorageImage(author.photo, 206, 208) : '';

    const maybeGenitiveName = author.genitive_full_name || author.cover_name;
    const title = `${author.cover_name}: читать онлайн лучшие книги. ` +
                  `${author.active_book_count} популярных книг автора в электронной библиотеке MyBook.ru`;
    const description = `Список лучших книг автора ${author.cover_name}, ` +
                        `биография автора, рейтинг и чтение онлайн книг в электроной библиотеке MyBook.ru`;
    const ogTitle = `${author.cover_name} — читать лучшие книги и новинки онлайн, на iPhone, iPad и Android — MyBook`;
    const ogDescription = `Читать онлайн, на телефоне или планшете книги ${maybeGenitiveName} ` +
                          `всего за ${standardPrice} рублей в месяц. ` +
                          `Лучшие и новые книги ${maybeGenitiveName} всегда под рукой.`;
    let ogTags = [
      { name: 'image', value: cover206x208 },
      { name: 'title', value: ogTitle },
      { name: 'url', value: desktopUrl },
    ];

    if (standardPrice) {
      ogTags.push({ name: 'description', value: ogDescription });
    }

    dispatch(SeoActions.setData({
      canonicalUrl: desktopUrl,
      imageSrc: cover206x208,
      title,
      description,
      ogTags,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { author } = payload;
    dispatch({
      type: CATALOG_AUTHOR_DETAIL_SET_DATA,
      payload,
    });
    // update meta tags every time the detail state is changed
    if (author) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch(SeoActions.resetData());
    dispatch({
      type: CATALOG_AUTHOR_DETAIL_RESET_DATA,
    });
  };
}
